import { BigNumber, ContractTransaction } from 'ethers';

/**
 * RPC配置
 * @interface RpcConfig
 * @property {number} chainId - 链ID，例如BSC的chainId为56
 * @property {string} url - RPC节点URL
 * @property {string} name - 链的名称，用于显示
 * @property {string} symbol - 链的原生代币符号，如 BNB, ETH
 */
export interface RpcConfig {
  chainId: number;
  url: string;
  name: string;
  symbol?: string;
}

/**
 * 路由器配置
 * @interface RouterConfig
 * @property {string} address - 路由器合约地址
 * @property {string} name - 路由器名称，如 "PancakeSwap V2"
 */
export interface RouterConfig {
  address: string;
  name: string;
}

/**
 * DEX配置信息
 * @interface DexConfig
 * @property {RouterConfig} router - 路由器配置
 * @property {string} [factoryAddress] - 工厂合约地址
 */
export interface DexConfig {
  router: RouterConfig;
  factory: {
    address: string;
  };
}

/**
 * 代币兑换参数
 * @interface SwapParams
 * @property {string} tokenIn - 输入代币地址
 * @property {string} tokenOut - 输出代币地址
 * @property {string} amountIn - 输入金额
 * @property {number} tokenInDecimals - 输入代币精度
 * @property {number} tokenOutDecimals - 输出代币精度
 * @property {number} slippage - 滑点百分比，例如0.5表示0.5%
 * @property {string} [gasPrice] - Gas价格
 * @property {string} [gasLimit] - Gas限制
 * @property {number} [gasPriceBoost] - Gas价格提升倍数
 * @property {number} [deadline] - 交易截止时间戳（秒）
 * @property {BigNumber} [maxBuyAmount] - Four.meme路由器专用：最大购买代币数量
 * @property {number} [walletIndex] - 指定使用的钱包索引，便于前端直接指定钱包
 */
export interface SwapParams {
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  tokenInDecimals: number;
  tokenOutDecimals: number;
  slippage: number;
  gasPrice?: string;
  gasLimit?: string;
  gasPriceBoost?: number;
  deadline?: number;
  maxBuyAmount?: BigNumber;
  walletIndex?: number;
}


// 钱包类型定义
export interface Wallet {
  id: string;
  address: string;
  bnbBalance?: string;
  usdtBalance?: string;
  nonce?: number;
  status?: 'pending' | 'success' | 'error';
  errorMessage?: string;
}

/**
 * 代币兑换结果
 */
export interface SwapResult {
  success: boolean;
  txHash?: string;
  amountOut?: BigNumber;
  gasUsed?: BigNumber;
  effectiveGasPrice?: BigNumber;
  error?: string;
}

/**
 * 代币兑换预估结果
 */
export interface SwapEstimation {
  amountOut: BigNumber;
  priceImpact: number;
}

/**
 * 钱包配置
 * @interface WalletConfig
 * @property {string} privateKey - 钱包私钥
 * @property {string} [address] - 可选的钱包地址，如果不提供会从私钥派生
 * @property {string} [bnbBalance] - BNB余额
 * @property {string} [usdtBalance] - USDT余额
 * @property {string} [token1Balance] - Token1余额
 * @property {string} [token2Balance] - Token2余额
 * @property {number} [nonce] - 钱包交易计数
 * @property {'pending' | 'success' | 'error'} [status] - 钱包状态
 * @property {string} [errorMessage] - 错误信息
 */
export interface WalletConfig {
  privateKey: string;
  address: string;
  bnbBalance?: string;
  usdtBalance?: string;
  token1Balance?: string;
  token2Balance?: string;
  nonce?: number;
  status?: 'pending' | 'success' | 'error';
  errorMessage?: string;
}

/**
 * 日志级别
 */
export enum LogLevel {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS'
}

/**
 * 创建日志的参数
 */
export interface CreateLogParams {
  level: LogLevel;
  message: string;
  walletIndex?: number | null;
  data?: any;
}

/**
 * 日志数据
 */
export interface LogData {
  level: LogLevel;
  message: string;
  timestamp: number;
  walletIndex?: number | null;
  data?: any;
  state?: any;
}

/**
 * Four.meme路由器配置
 * @interface FourMemeRouterConfig
 * @property {string} address - 路由器合约地址
 * @property {string[]} supportedTokens - 支持的代币地址列表
 */
export interface FourMemeRouterConfig {
  address: string;
  supportedTokens: string[];
}

/**
 * Gas设置模式
 */
export enum GasSettingMode {
  AUTO = 'auto',            // 自动设置Gas
  BASIC = 'basic',          // 基础设置
  ADVANCED = 'advanced'     // 高级设置
}

/**
 * 网络拥堵级别枚举
 */
export enum NetworkCongestionLevel {
  LOW = 'low',              // 低拥堵
  MEDIUM = 'medium',        // 中等拥堵
  HIGH = 'high'             // 高拥堵
}

/**
 * Gas价格信息
 */
export interface GasPrices {
  low: string;
  medium: string;
  high: string;
  baseFeePerGas?: string;
}

/**
 * Gas设置配置
 */
export interface GasSettingsConfig {
  mode: GasSettingMode;
  congestionLevel: NetworkCongestionLevel;
  gasPrice: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  gasLimit: string;
  currentPrices?: GasPrices;
}

/**
 * DexService配置
 * @interface DexServiceConfig
 * @property {RpcConfig} rpc - RPC配置
 * @property {DexConfig} dex - DEX配置
 * @property {WalletConfig[]} wallets - 钱包配置列表
 * @property {(log: LogData) => void} [onLog] - 日志回调函数
 * @property {FourMemeRouterConfig} [fourMemeRouter] - Four.meme路由器配置
 * @property {GasSettingsConfig} [gasSettings] - Gas设置配置
 */
export interface DexServiceConfig {
  rpc: RpcConfig;
  dex: DexConfig;
  wallets: WalletConfig[];
  onLog?: (log: LogData) => void;
  gasSettings?: GasSettingsConfig;
  maxThreads?: number;
}

/**
 * 代币信息
 * @interface TokenInfo
 * @property {string} name - 代币名称
 * @property {string} symbol - 代币符号
 * @property {number} decimals - 代币小数位数
 * @property {BigNumber} totalSupply - 代币总供应量
 */
export interface TokenInfo {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply?: BigNumber;
  logoUrl?: string;
}

/**
 * 队列运行模式
 */
export enum QueueRunMode {
  SERIAL = '串行',  // 串行执行，一个接一个
  PARALLEL = '并行'  // 并行执行，同时执行多个
}

/**
 * 队列停止条件
 */
export enum QueueStopCondition {
  RUN_COUNT = '运行次数',  // 运行指定次数后停止
  PRICE_INCREASE = '价格上涨时',  // 价格上涨到指定比例时停止
  PRICE_DECREASE = '价格下跌时'  // 价格下跌到指定比例时停止
}

/**
 * 代币信息接口
 */
export interface TokenInfo {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoUrl?: string;
}

/**
 * 交换配置接口
 */
export interface SwapConfig {
  token1: TokenInfo | null;
  token2: TokenInfo | null;
  slippage: number;
  amountType: string;  // 金额类型：ALL, RANDOM, RANDOM_PERCENT, FIXED, RESERVE
  minAmount?: string;  // 随机金额最小值
  maxAmount?: string;  // 随机金额最大值
  fixedAmount?: string; // 固定金额
  reserveAmount?: string; // 保留金额
}

/**
 * 队列配置接口
 */
export interface QueueConfig {
  interval: number;    // 间隔时间(秒)
  threads: number;     // 线程数量
  taskCount: number;   // 任务数量
  runMode: QueueRunMode;  // 运行模式：串行或并行
  stopCondition: QueueStopCondition;  // 停止条件
  stopValue: number;   // 停止值 (运行次数或价格变化百分比)
}

/**
 * 其他配置接口
 */
export interface OtherConfig {
  gasPrice: number;
  gasLimit: number;
}

/**
 * 任务配置接口
 */
export interface TaskConfig {
  swap: SwapConfig;
  queue: QueueConfig;
  other: OtherConfig;
}

/**
 * 金额设置类型
 */
export enum AmountType {
  /** 全部金额 */
  ALL = 'ALL',
  /** 随机金额 */
  RANDOM = 'RANDOM',
  /** 随机百分比 */
  RANDOM_PERCENT = 'RANDOM_PERCENT',
  /** 固定金额 */
  FIXED = 'FIXED',
  /** 保留固定金额 */
  RESERVE = 'RESERVE'
}

/**
 * 钱包执行顺序
 */
export enum WalletExecutionOrder {
  /** 顺序执行 */
  SEQUENTIAL = 'SEQUENTIAL',
  /** 随机执行 */
  RANDOM = 'RANDOM'
}

/**
 * DexService接口
 * @interface DexService
 */
export interface DexService {
  provider: any;
  config: DexServiceConfig;
  
  /**
   * 获取代币信息
   * @param {string} tokenAddress - 代币合约地址
   * @returns {Promise<TokenInfo>} 代币信息
   */
  getTokenInfo(tokenAddress: string): Promise<TokenInfo>;
  
  /**
   * 获取代币授权额度
   * @param {string} tokenAddress - 代币合约地址
   * @param {string} owner - 持有者地址
   * @param {string} spender - 被授权者地址
   * @returns {Promise<BigNumber>} 授权额度
   */
  getAllowance(tokenAddress: string, owner: string, spender: string): Promise<BigNumber>;
  
  /**
   * 授权代币
   * @param {string} tokenAddress - 代币合约地址
   * @param {string} spender - 被授权者地址
   * @param {BigNumber} amount - 授权金额
   * @param {string} owner - 持有者地址
   * @returns {Promise<ContractTransaction>} 交易结果
   */
  approve(
    tokenAddress: string,
    spender: string,
    amount: BigNumber,
    owner: string
  ): Promise<ContractTransaction>;
  
  /**
   * 获取代币余额
   * @param {string} tokenAddress - 代币合约地址
   * @param {string} walletAddress - 钱包地址
   * @returns {Promise<BigNumber>} 代币余额
   */
  checkTokenBalance(tokenAddress: string, walletAddress: string): Promise<BigNumber>;

  /**
   * 获取原生代币余额
   * @param {string} address - 钱包地址
   * @returns {Promise<BigNumber>} 原生代币余额
   */
  getNativeBalance(address: string): Promise<BigNumber>;
  
  /**
   * 获取兑换预估
   * @param {SwapParams} params - 兑换参数
   * @returns {Promise<SwapEstimation>} 预估结果
   */
  getAmountOut(params: SwapParams): Promise<SwapEstimation>;
  
  /**
   * 兑换代币
   * @param {SwapParams} params - 兑换参数
   * @param {string} walletAddress - 钱包地址
   * @returns {Promise<SwapResult>} 兑换结果
   */
  swap(params: SwapParams, walletAddress: string): Promise<SwapResult>;

  /**
   * 批量兑换代币
   * @param {SwapParams} params - 兑换参数
   * @returns {Promise<Array<{ walletIndex: number; success: boolean; result?: SwapResult; error?: string; }>>} 批量兑换结果
   */
  batchSwap(params: SwapParams): Promise<Array<{
    walletIndex: number;
    success: boolean;
    result?: SwapResult;
    error?: string;
  }>>;
}
